import { Component, Input, ViewChild, ElementRef, AfterViewInit, ViewChildren, HostBinding } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { Transfer, PageComponent, AppConfig, SiteWideAlert, sleep } from '@tytapp/common';
import { LiveStream, User, Show } from '@tytapp/models';
import { UserService } from '@tytapp/user';
import { environment } from 'environment';
import { LiveStreamsService } from 'common-ui/live-streams.service';
import { Subscription } from 'rxjs';
import { PageService, Page } from '@tytapp/common';
import { GoogleCastService } from 'media-playback';
import { ShowsSidebarComponent } from 'common-ui';
import { QueryList } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';

@Page('tyt-nav')
@Component({
  selector: 'tyt-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss']
})
export class NavComponent extends PageComponent implements AfterViewInit {
  // TODO: this class should not be PageComponent but it relies on state transfer
  
  constructor(
    private pageService : PageService,
    private location : Location,
    private element : ElementRef,
    private userService : UserService,
    private liveStreamsService : LiveStreamsService,
    private castService : GoogleCastService,
    private appConfig : AppConfig,
    private domSanitizer : DomSanitizer
  ) { 
    super(pageService);
    this.subscription = this.router.events.subscribe(s => {
      if (s instanceof NavigationEnd) {
        this.update();
      }
    });
  }

  @HostBinding('class.animate-in')
  animateIn = false;

  get casting() {
    return this.castService.connected;
  }
  
  get pageComponentId() {
    return 'tyt-nav';
  }

  dismissAlert(alert : SiteWideAlert) {
    this.shell.dismissAlert(alert.id);
  }

  showAccountMenu : boolean = false;
  sidebarVisible : boolean = false;
  highlightMap = {};
  siteWideMessage = '';
  siteWideUrl : SafeHtml = null;

  private subscription: Subscription;
  
  @ViewChildren(ShowsSidebarComponent)
  showsSidebars : QueryList<ShowsSidebarComponent>;

  async signOut() {
    this.showAccountMenu = false;
    this.userService.completeLogout();
    this.userService.user = null;
    this.router.navigate(['home']);
  }

  get userGreeting() {
    let displayName = this.userDisplayName;

    if (displayName) 
      return `Hi ${displayName}!`;
    else 
      return `Hi!`;
  }

  get accountsUrl() {
    return environment.urls.accounts;
  }
  
  get userDisplayName() {
    if (!this.user)
      return null;
    
    if (!this.user.profile)
      return null;
    
    if (this.user.profile.display_name)
      return this.user.profile.display_name;
    
    if (this.user.profile.first_name)
      return this.user.profile.first_name;
    
    if (this.user.display_name)
      return this.user.display_name;

    return null;
  }

  alerts : SiteWideAlert[] = [];

  communityEnabled = false;

  async init() {
    this.shell.alertsChanged.subscribe(alerts => this.alerts = alerts);

    this.communityEnabled = await this.appConfig.featureEnabled('apps.web.enable_community');

    this.router.events.subscribe(ev => {
      this.sidebarVisible = false;
      this.showAccountMenu = false;
    });

    await this.userService.ready;
    
    this.subscribe(this.userService.userChanged, (user) => {
      if (!this.hasServerState) {
        if (user && user.membership) {
          if (user.membership.payment_status === 'past_due') {
            this.isPastDue = true;
          }
        }
        this.user = user;
      }
    })

    try {
      this.isLive = await this.liveStreamsService.getLiveIndicator();
    } catch (e) {
      console.error(`While fetching the live streams in NavComponent:`);
      console.error(e);
    }

    await sleep(1);
    this.animateIn = true;
  }

  get alertWideUrl() {
    return this.siteWideUrl || 'javascript:;'
  }

  openSiteWideUrlWindow() {
    if (typeof window !== 'undefined') {
      window.location.href = 'www.youtube.com';
    }
  }

  showsDropdownVisible : boolean = false;

  revealShowsDropdown() {
    if (this.showsDropdownVisible) {
      this.router.navigate(['shows']);
    } else {
      this.showsDropdownVisible = true;
    
      let element : HTMLElement = this.element.nativeElement;
      let dropContentElement = element.querySelector('.drop-content');
      
      if (this.showsSidebars && this.showsSidebars.length > 0)
        this.showsSidebars.first.reset();

      setTimeout(() => {
        dropContentElement.scrollTo({ top: 0 });
      }, 1);
    }
  }

  hideShowsDropdown(closeIconTapped = false) {
    this.showsDropdownVisible = false;
  }
  
  update() {
    let currentUrl = this.router.url;
    let urlArray = currentUrl.split('/');
    this.highlightMap = {};

    if (urlArray.length >= 2 && ['shows', 'watch', 'listen'].includes(urlArray[1])) {
      this.highlightMap['shows'] = true;
    }

    this.hideShowsDropdown();
  }

  @Transfer()
  user : User;

  @Transfer()
  isLive : boolean = false;
  
  @Transfer()
  isPastDue: boolean = false;

  ngAfterViewInit() {
  }
  
  destroy(): any { this.subscription.unsubscribe(); }

  @Input()
  menuOpen : boolean = false;

  @Input()
  noLayout : boolean = false;

  @Transfer()
  shows : Show[];

  @Transfer()
  podcasts : Show[];
  
  @Transfer()
  streams : LiveStream[];

  get joinUrl() {
    // CAUTION: THIS SHOULD BE CHANGED GLOBALLY (at appConfig) NOT HERE
    return environment.urls.purchase;
  }

  get platformUrl() {
    return `${environment.urls.platform}`;
  }
  
  get accountUrl() {
    return `${environment.urls.accounts}/account`;
  }

  get communicationPrefUrl() {
    return `${environment.urls.accounts}/account/communication-pref`;
  }

  get profileUrl() {
    return `${environment.urls.accounts}/profile`;
  }

  @ViewChild('menu', { static: true })
  menu : ElementRef;

  subsection : string = '';
  private streamsFetched = false;
  
  private pendingShow = null;
  private pendingSubShow = null;

  liveLastFetched : Date = null;

  public search() {
    // TODO: OK show the search page or something
    this.shell.showDialog(MessageDialogComponent, 'Search Coming Soon', `Search is not yet implemented! We would search for '${this.searchText}' though if we could!`);
  }

  public searchChanged(value) {
    // console.log('Search changed: '+value);
  }

  searchText : string;

  private currentSliderUrl : string;
  private hoverSliderUrl : string;

  enterSectionLink(el : Element) {
  }

  leaveSectionLink() {
    clearTimeout(this.pendingShow);
  }
}
